import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';

import { UserState } from '../../../reducers/user';
import { useDynamicFormContext } from '../context';
import { FieldProps } from '../types';
import RadioList from './RadioList';

const useStyles = makeStyles(() => ({
  answer: {
    margin: 0,
  },
}));

function SingleChoiceField(props: FieldProps) {
  const { field, answer } = props.filledOutField;

  const classes = useStyles();
  const { roleView } = useSelector((selector: { user: UserState }) => ({
    roleView: selector.user.roleView,
  }));

  const { setSelectedOptionIds, setAnswerValues, formIsReadOnly } =
    useDynamicFormContext();

  if (roleView === null) {
    throw new Error('Invalid session');
  }

  if (field.discr !== 'singlechoicefield') {
    throw new Error('Unsupported field');
  }

  if (answer && answer.discr !== 'choicefieldanswer') {
    throw new Error('Unsupported answer');
  }

  const options = field.options.map((option) => ({
    key: option.id,
    value: option.id,
    label: option.label,
  }));

  const chosenOptionId = answer ? answer.chosenOptions[0].id : undefined;

  const onValueChangeHandler = useCallback(
    (value: number | undefined) => {
      const optionIds = field.options.map((option) => option.id);
      const chosenOptionIds = value ? [value] : [];

      setSelectedOptionIds((prev) => {
        const updated = [...prev];

        return [
          // current selection minus options for this field
          ...updated.filter((id) => !optionIds.includes(id)),
          // selected options for this field
          ...chosenOptionIds.filter((id) => optionIds.includes(id)),
        ];
      });

      setAnswerValues((prev) => ({
        ...prev,
        [String(field.id)]: chosenOptionIds,
      }));
    },
    [field.id, field.options, setSelectedOptionIds, setAnswerValues],
  );

  if (formIsReadOnly || props.filledOutField.field.readOnly) {
    return (
      <p className={classes.answer}>
        {answer?.chosenOptions.find((option) => chosenOptionId === option.id)
          ?.label || 'Onbekend'}
      </p>
    );
  }

  return (
    <RadioList<number>
      options={options}
      defaultValue={chosenOptionId}
      onValueChange={onValueChangeHandler}
    />
  );
}

export default SingleChoiceField;
